/**
 * API documentation — /api-docs (REPSit sidebar → Admin → API; DREW sidebar → DREW API Docs).
 * Everything rendered here comes from `platform/api/apiReference.js`, which also generates
 * `docs/api/openapi.yaml`. Implementation: `functions/src/hugoApi.js`.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Chip, Button, Divider, Tabs, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, IconButton, Alert, Link,
} from '@mui/material';
import CodeIcon from '@mui/icons-material/Code';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { useAuth } from '../contexts/AuthContext';
import { useDemoRole } from '../contexts/DemoRoleContext';
import { canSeeApi } from '../platform/navigation/navigation.js';
import { useUserOrganizations } from '../assistants/drew/hooks/useOrganization';
import { API_VERSION, BASE_PATH, ENDPOINTS, ERROR_CODES, SCOPES, VERIFY_SIGNATURE_SAMPLES, WEBHOOK_EVENTS } from '../platform/api/apiReference';

const METHOD_COLOR = { GET: '#0B5443', POST: '#16699F', DELETE: '#B23A2B' };

function Code({ children, lang = 'bash', copy = true }) {
  const [copied, setCopied] = useState(false);
  const text = typeof children === 'string' ? children : String(children);
  const onCopy = async () => { try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* fine */ } };
  return (
    <Box sx={{ position: 'relative', my: 1 }}>
      <Box component="pre" data-lang={lang} sx={{ m: 0, p: 1.5, pr: 5, bgcolor: (t) => (t.palette.mode === 'dark' ? '#0f1020' : '#1E2360'), color: '#E3E5F6', borderRadius: 1.5, fontSize: '0.8rem', lineHeight: 1.55, overflowX: 'auto', fontFamily: '"Roboto Mono", monospace' }}>
        {text}
      </Box>
      {copy && (
        <Tooltip title={copied ? 'Copied' : 'Copy'}>
          <IconButton size="small" onClick={onCopy} sx={{ position: 'absolute', top: 6, right: 6, color: '#E3E5F6', opacity: 0.8 }} aria-label="Copy code"><ContentCopyIcon fontSize="small" /></IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

const json = (v) => JSON.stringify(v, null, 2);

function curlFor(ep, origin) {
  const url = `${origin}${BASE_PATH}${ep.path.replace('/v1', '').replace('{id}', '26-HOU-0007')}`;
  const lines = [`curl -X ${ep.method} "${url}"`, `  -H "Authorization: Bearer $REPSIT_API_KEY"`];
  if (ep.request) { lines.push('  -H "Content-Type: application/json"'); lines.push(`  -d '${JSON.stringify(ep.request)}'`); }
  return lines.join(' \\\n');
}

function ParamTable({ rows, cols }) {
  return (
    <Box sx={{ overflowX: 'auto', my: 1 }}>
      <Table size="small">
        <TableHead><TableRow>{cols.map((c) => <TableCell key={c}>{c}</TableCell>)}</TableRow></TableHead>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={i}>
              {r.map((cell, j) => (
                <TableCell key={j} sx={j === 0 ? { fontFamily: '"Roboto Mono", monospace', fontSize: '0.8rem', whiteSpace: 'nowrap' } : { fontSize: '0.85rem' }}>{cell}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function Endpoint({ ep, origin }) {
  const [tab, setTab] = useState(0);
  return (
    <Box id={`ep-${ep.id}`} sx={{ scrollMarginTop: 96, py: 2.5, borderBottom: 1, borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
        <Chip label={ep.method} size="small" sx={{ bgcolor: METHOD_COLOR[ep.method], color: '#fff', fontWeight: 800, fontFamily: '"Roboto Mono", monospace', minWidth: 64 }} />
        <Typography sx={{ fontFamily: '"Roboto Mono", monospace', fontWeight: 700, fontSize: '0.95rem', wordBreak: 'break-all' }}>{BASE_PATH}{ep.path.replace('/v1', '')}</Typography>
        {ep.scope ? <Chip size="small" variant="outlined" icon={<VpnKeyIcon sx={{ fontSize: 14 }} />} label={ep.scope} sx={{ fontSize: '0.7rem' }} /> : <Chip size="small" variant="outlined" label="any key" sx={{ fontSize: '0.7rem' }} />}
        {ep.status && <Chip size="small" label={`→ ${ep.status}`} sx={{ fontSize: '0.7rem' }} />}
      </Box>
      <Typography variant="subtitle1" fontWeight={700}>{ep.summary}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720, mb: 1 }}>{ep.desc}</Typography>
      {ep.params && <ParamTable cols={['Parameter', 'In', 'Description']} rows={ep.params} />}
      {ep.body && <ParamTable cols={['Field', 'Type', 'Required', 'Description']} rows={ep.body} />}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5, textTransform: 'none', fontWeight: 700 } }}>
        <Tab label="curl" />
        {ep.request && <Tab label="Request body" />}
        {ep.response && <Tab label="Response" />}
      </Tabs>
      {tab === 0 && <Code>{curlFor(ep, origin)}</Code>}
      {ep.request && tab === 1 && <Code lang="json">{json(ep.request)}</Code>}
      {ep.response && tab === (ep.request ? 2 : 1) && <Code lang="json">{json(ep.response)}</Code>}
    </Box>
  );
}

const TOC = [
  ['overview', 'Overview'],
  ['auth', 'Authentication'],
  ['scopes', 'Permissions (scopes)'],
  ['conventions', 'Conventions'],
  ['rate-limits', 'Rate limits'],
  ['errors', 'Errors'],
  ...ENDPOINTS.map((g) => [`group-${g.group.toLowerCase().replace(/[^a-z]+/g, '-')}`, g.group]),
  ['webhooks', 'Webhooks'],
  ['recipes', 'Recipes'],
  ['changelog', 'Changelog'],
];

export default function ApiDocs() {
  const navigate = useNavigate();
  const { hash } = useLocation();
  const { user } = useAuth();
  const { persona } = useDemoRole();
  const { organizations } = useUserOrganizations(user?.uid);
  const org = organizations[0] || null;
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://app.repsit.ai';

  useEffect(() => {
    if (!hash) return;
    const t = setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 200);
    return () => clearTimeout(t);
  }, [hash]);

  const H = ({ id, children }) => <Typography id={id} variant="h5" fontWeight={800} sx={{ mt: 5, mb: 1, scrollMarginTop: 96 }}>{children}</Typography>;
  const P = ({ children }) => <Typography variant="body1" sx={{ maxWidth: 720, mb: 1.25 }}>{children}</Typography>;

  const nav = useMemo(() => (
    <Box component="nav" sx={{ position: { md: 'sticky' }, top: { md: 88 }, alignSelf: 'flex-start', minWidth: 200, display: { xs: 'none', md: 'block' } }}>
      <Typography variant="overline" color="text.secondary">On this page</Typography>
      {TOC.map(([id, label]) => (
        <Link key={id} href={`#${id}`} underline="hover" color="text.primary" sx={{ display: 'block', fontSize: '0.85rem', py: 0.4 }}>{label}</Link>
      ))}
    </Box>
  ), []);

  // Tim (1 Nov): the API and its documentation are for Org IT and Administrators only.
  if (!canSeeApi(persona?.id)) {
    return (
      <Box sx={{ maxWidth: 720, mx: 'auto', mt: 4 }}>
        <Alert severity="info">
          The REPSit.ai API and its documentation are available to your organization's IT contact (Org IT) and Administrators. Ask them if you need a connection set up.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1180, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5, flexWrap: 'wrap' }}>
        <CodeIcon sx={{ fontSize: 36, color: 'primary.main' }} />
        <Typography variant="h4" fontWeight={800} sx={{ flex: 1, minWidth: 200 }}>REPSit.ai API</Typography>
        <Chip label={`v1 · ${API_VERSION}`} size="small" variant="outlined" />
        <Button variant="contained" size="small" startIcon={<VpnKeyIcon />} onClick={() => navigate('/settings/organization#api-access')} sx={{ textTransform: 'none', fontWeight: 700 }}>Manage API keys</Button>
      </Box>
      <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 760, mb: 3 }}>
        Connect the systems you already run — BI dashboards, a CMMS, an incident tracker, Power Automate, n8n — to your organization’s Organizational Entropy data.
        Read scores and conditions, log conditions from other tools, and receive events the moment they happen.
      </Typography>

      <Box sx={{ display: 'flex', gap: 4, alignItems: 'flex-start' }}>
        {nav}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <H id="overview">Overview</H>
          <P>The API is REST over HTTPS with JSON bodies. Every request is made on behalf of <strong>one organization</strong> — the one the key belongs to — so there is no organization id in the URL. Base URL:</P>
          <Code copy>{`${origin}${BASE_PATH}`}</Code>
          <P>Three things you can do today: <strong>read</strong> (structure, members, OE scores and history, conditions, alerts, surveys, the library), <strong>write</strong> (log an OE Condition that enters the normal L1 → L2 review), and <strong>subscribe</strong> (webhooks for conditions, alerts and score updates). Survey responses are anonymous and are never exposed.</P>
          <Alert severity="info" sx={{ maxWidth: 720 }}>During the beta, API keys work against your sandbox organization. When you import your real organization, keys are issued for it separately.</Alert>

          <H id="auth">Authentication</H>
          <P>An administrator creates keys under <Link href="#" onClick={(e) => { e.preventDefault(); navigate('/settings/organization#api-access'); }}>Settings → Organization → API access</Link>. A key looks like <code>rsk_live_…</code>, is shown once, and is sent as a bearer token:</P>
          <Code>{`curl "${origin}${BASE_PATH}/me" \\\n  -H "Authorization: Bearer rsk_live_0123abcd…"`}</Code>
          <P>Keys are stored hashed — we cannot recover one for you. If a key leaks, revoke it in Settings; anything using it stops at once. Give each integration its own key with the smallest set of permissions it needs, and set an expiry for keys used in trials.</P>

          <H id="scopes">Permissions (scopes)</H>
          <P>Each key carries a list of scopes. A route needs exactly one; a key without it gets <code>403 insufficient_scope</code>.</P>
          <ParamTable cols={['Scope', 'Grants', 'What it covers']} rows={SCOPES.map((s) => [s.key, s.title, s.desc])} />

          <H id="conventions">Conventions</H>
          <P>Timestamps are ISO‑8601 in UTC. Ids are opaque strings; conditions also carry a human id <code>YY‑SSS‑NNNN</code> (year, site code, sequence) that you can use anywhere an id is accepted. Scores are 0–10 where <strong>higher means more Organizational Entropy</strong> — worse. Lists return <code>{'{ data: [...], count }'}</code>, newest first, with <code>limit</code> capped at 200; use <code>since</code> to page by time. Every response carries <code>X-REPSit-Api-Version</code>; breaking changes get a new version, additions do not.</P>

          <H id="rate-limits">Rate limits</H>
          <P>120 requests per minute per key by default. Every response includes <code>X-RateLimit-Limit</code>, <code>X-RateLimit-Remaining</code> and <code>X-RateLimit-Reset</code> (unix seconds). Over the limit you receive <code>429 rate_limited</code>; wait for the reset and retry. Need more for a bulk load? Tell the build team.</P>

          <H id="errors">Errors</H>
          <P>Errors are JSON with a stable <code>code</code> and a human message; validation errors list every problem at once:</P>
          <Code lang="json">{json({ error: { code: 'validation_failed', message: 'The condition could not be created.', details: { errors: ['nwtId does not belong to this organization', 'repsQuadrants must be a non-empty array of reliability, efficiency, productivity, safety'] } } })}</Code>
          <ParamTable cols={['HTTP', 'code', 'Meaning']} rows={ERROR_CODES} />

          {ENDPOINTS.map((g) => (
            <Box key={g.group}>
              <H id={`group-${g.group.toLowerCase().replace(/[^a-z]+/g, '-')}`}>{g.group}</H>
              {g.items.map((ep) => <Endpoint key={ep.id} ep={ep} origin={origin} />)}
            </Box>
          ))}

          <H id="webhooks">Webhooks</H>
          <P>Register an HTTPS endpoint with <code>POST /v1/webhooks</code> and REPSit.ai sends a POST for each event you subscribed to. Deliveries time out after 8 seconds; respond 2xx quickly and do the work afterwards. After 25 consecutive failures the endpoint is paused.</P>
          <ParamTable cols={['Event', 'When it fires', 'Payload']} rows={WEBHOOK_EVENTS.map((e) => [e.type, e.when, e.payload])} />
          <Typography variant="subtitle2" sx={{ mt: 2 }}>Delivery format</Typography>
          <Code lang="http">{`POST https://hooks.example.com/repsit
Content-Type: application/json
X-REPSit-Event: condition.created
X-REPSit-Delivery: 5d2c9f1e7a3b4c6d8e9f0a1b
X-REPSit-Timestamp: 1790080000
X-REPSit-Signature: v1=6a1f…c9e2

{
  "id": "5d2c9f1e7a3b4c6d8e9f0a1b",
  "type": "condition.created",
  "createdAt": "2026-09-22T13:04:12.000Z",
  "organizationId": "org_magnum_t01",
  "data": { "id": "Ab12Cd34Ef56", "conditionId": "26-HOU-0007", "type": "trap", "title": "Tool crib closes during turnover", "status": "submitted", "…": "…" }
}`}</Code>
          <Typography variant="subtitle2" sx={{ mt: 2 }}>Verifying the signature</Typography>
          <P>The signature is HMAC‑SHA256 of <code>{'<timestamp>.<raw body>'}</code> with the webhook’s secret. Reject deliveries older than five minutes and compare in constant time:</P>
          <Code lang="js">{VERIFY_SIGNATURE_SAMPLES.node}</Code>
          <Code lang="python">{VERIFY_SIGNATURE_SAMPLES.python}</Code>

          <H id="recipes">Recipes</H>
          <Typography variant="subtitle2">Power BI / Excel — OE trend per site</Typography>
          <P>Use a Web data source with the header <code>Authorization: Bearer …</code> against <code>/v1/snapshots?scope=site:&lt;id&gt;&amp;limit=200</code>; expand <code>data</code> → <code>scores</code>. Refresh daily — snapshots are recalculated after surveys close and after conditions change state.</P>
          <Typography variant="subtitle2">CMMS or incident system → OE Conditions</Typography>
          <P>Map your work‑order categories to <code>type</code> and <code>trapCategory</code>, look up the crew’s <code>nwtId</code> once from <code>/v1/organization</code>, and POST <code>/v1/conditions</code> with your ticket number in <code>externalRef</code>. Supervisors then see it in their L1 queue like any other entry.</P>
          <Typography variant="subtitle2">Teams / Slack alerts with n8n or Power Automate</Typography>
          <P>Register the flow’s webhook URL with events <code>alert.created</code> and <code>condition.status_changed</code>, verify the signature in a Code step, and post <code>data.title</code> and <code>data.severity</code> to the channel.</P>
          <Typography variant="subtitle2">OpenAPI</Typography>
          <P>A machine‑readable spec is published with the app at <Link href="/openapi.yaml" target="_blank" rel="noopener">/openapi.yaml</Link> — import it into Postman, Insomnia or an SDK generator.</P>

          <H id="changelog">Changelog</H>
          <P><strong>{API_VERSION}</strong> — first release: organization, members, scores, snapshots, conditions (read + create), alerts, surveys, case studies, webhooks with signed deliveries.</P>
          <Divider sx={{ my: 3 }} />
          <Typography variant="body2" color="text.secondary">Questions, missing fields, a limit in your way? Use <strong>Tell the Build Team</strong> in the header — two humans read every note.{org ? ` Your organization: ${org.name}.` : ''}</Typography>
        </Box>
      </Box>
    </Box>
  );
}
